import { Injectable } from '@angular/core';
import cloneDeep from 'lodash.clonedeep';

import {
  DprPreviousValuesSet,
  DprSectionsWithoutHistory,
  DprStatusTrackerAccumulation,
  DprTableOfColumnsAccumulation,
  StatusTrackerHistoryItem,
  TableOfColumnHistoryItem,
} from '@dpr-details/models';
import {
  applyCarryOverValues,
  buildPreviousDpr,
  buildStatusTrackerFormulaHistory,
  buildStatusTrackerPreviousValues,
  buildTableOfColumnsFormulaHistory,
  buildTableOfColumnsPreviousValues,
  computeStatusTrackerAccumulation,
  computeTableOfColumnsAccumulation,
  FormulaPreviousValuePrecision,
  getHistoryColumnIds,
  getRowAnchorValue,
  getSectionsWithoutPreviousValues,
  getStatusTrackerCellKey,
  getTableOfColumnsPreviousValueItemIds,
  indexStatusTrackerCells,
} from '@dpr-details/utils';
import { ProjectSetup } from '@shared/models';
import {
  DprDetails,
  DprStatusTracker,
  DprStatusTrackerDetails,
  DprTableOfColumnDetail,
} from '@shared/models/dpr';
import { DprFormulaCalculationService } from '../formula';

/**
 * Calculates the carry-over and formula cells of a stored DPR, the way opening it
 * calculates them, without the grids.
 */
@Injectable()
export class DprCarryOverFormulaCalculationService {
  constructor(
    private dprFormulaCalculationService: DprFormulaCalculationService
  ) {}

  /**
   * A copy of the DPR with its carry-overs and formulas calculated from what was
   * typed on it and its previous values, or the previous values given instead. A
   * DPR nobody saved the Status Tracker on is calculated as an empty one, so its
   * carry-overs and running totals still reach the DPR after it, though it has
   * nowhere to keep its own previous values. A section that needs previous values
   * and came without the field keeps the carry-overs and history-dependent formulas
   * stored on it, as they cannot be calculated without its history.
   */
  calculate(
    projectSetup: ProjectSetup,
    dprDetails: DprDetails,
    previousValues?: DprPreviousValuesSet
  ): DprDetails {
    const calculatedDpr: DprDetails = {
      ...dprDetails,
      dprTableOfColumns: cloneDeep(dprDetails.dprTableOfColumns ?? []),
      dprStatusTracker: cloneDeep(dprDetails.dprStatusTracker) ?? {
        id: null,
        dprDetailId: dprDetails.id,
        dprStatusTrackerDetails: [],
      },
    };

    if (previousValues) {
      this.applyPreviousValues(calculatedDpr, previousValues);
    }

    const previousDpr = buildPreviousDpr(
      calculatedDpr,
      projectSetup.tableOfColumns
    );
    const sectionsWithoutHistory = getSectionsWithoutPreviousValues(
      calculatedDpr,
      projectSetup.statusTrackers,
      projectSetup.tableOfColumns
    );
    const tableOfColumnsAccumulations = computeTableOfColumnsAccumulation(
      projectSetup.tableOfColumns,
      previousDpr
    );
    const statusTrackerAccumulations = computeStatusTrackerAccumulation(
      projectSetup.statusTrackers,
      previousDpr
    );

    this.fillCarryOvers(
      calculatedDpr,
      tableOfColumnsAccumulations,
      statusTrackerAccumulations,
      sectionsWithoutHistory
    );
    this.fillFormulas(
      projectSetup,
      calculatedDpr,
      previousDpr ? buildTableOfColumnsFormulaHistory(previousDpr) : [],
      previousDpr ? buildStatusTrackerFormulaHistory(previousDpr) : [],
      tableOfColumnsAccumulations,
      statusTrackerAccumulations,
      sectionsWithoutHistory
    );

    return calculatedDpr;
  }

  /**
   * The previous values the DPR after the given one starts from. With no DPR
   * before it they are null, as the API sends for a first DPR.
   */
  buildPreviousValues(
    projectSetup: ProjectSetup,
    calculatedPreviousDpr: DprDetails | null
  ): DprPreviousValuesSet {
    return {
      statusTracker: calculatedPreviousDpr
        ? buildStatusTrackerPreviousValues(
            projectSetup.statusTrackers,
            calculatedPreviousDpr.dprStatusTracker
          )
        : null,
      tableOfColumns: new Map(
        (projectSetup.tableOfColumns ?? [])
          .filter(
            (tableOfColumn) =>
              getTableOfColumnsPreviousValueItemIds(tableOfColumn).length
          )
          .map((tableOfColumn) => [
            +tableOfColumn.id,
            calculatedPreviousDpr
              ? buildTableOfColumnsPreviousValues(
                  tableOfColumn,
                  calculatedPreviousDpr.dprTableOfColumns?.find(
                    (x) => +x.tableOfColumnId === +tableOfColumn.id
                  )
                )
              : null,
          ])
      ),
    };
  }

  /**
   * Gives a DPR the previous values it starts from. A table with carry-overs or
   * running totals that the DPR has no entry for gets the empty entry the API sends
   * for such a table, so the values are there when its first row is added.
   */
  applyPreviousValues(
    dprDetails: DprDetails,
    previousValues: DprPreviousValuesSet
  ): void {
    if (
      dprDetails.dprStatusTracker &&
      previousValues.statusTracker !== undefined
    ) {
      dprDetails.dprStatusTracker.previousValues = previousValues.statusTracker;
    }

    previousValues.tableOfColumns.forEach((tableRows, tableOfColumnId) => {
      const dprTableOfColumn = dprDetails.dprTableOfColumns?.find(
        (x) => +x.tableOfColumnId === tableOfColumnId
      );

      if (dprTableOfColumn) {
        dprTableOfColumn.previousValues = tableRows;
        return;
      }

      dprDetails.dprTableOfColumns = [
        ...(dprDetails.dprTableOfColumns ?? []),
        {
          id: null,
          tableOfColumnId,
          dprDetailId: dprDetails.id,
          comment: null,
          dprTableOfColumnsDetails: [],
          dprTableOfColumnsAttachment: [],
          syncDprTableOfColumnsDetails: null,
          syncDprTableOfColumnsAttachments: null,
          previousValues: tableRows,
        },
      ];
    });
  }

  //#region Private methods

  /** Carry-overs of a section without history are left as stored. */
  private fillCarryOvers(
    dpr: DprDetails,
    tableOfColumnsAccumulations: Map<number, DprTableOfColumnsAccumulation>,
    statusTrackerAccumulations: Map<number, DprStatusTrackerAccumulation>,
    sectionsWithoutHistory: DprSectionsWithoutHistory
  ): void {
    dpr.dprTableOfColumns.forEach((dprTableOfColumn) => {
      const accumulation = tableOfColumnsAccumulations.get(
        +dprTableOfColumn.tableOfColumnId
      );

      if (
        !accumulation ||
        sectionsWithoutHistory.tableOfColumnIds.has(
          +dprTableOfColumn.tableOfColumnId
        )
      ) {
        return;
      }

      dprTableOfColumn.dprTableOfColumnsDetails?.forEach((detail) =>
        applyCarryOverValues(
          accumulation,
          getRowAnchorValue(detail, accumulation.anchorColumnId),
          (itemId, value) => this.setTableOfColumnValue(detail, itemId, value)
        )
      );
    });

    if (sectionsWithoutHistory.includesStatusTracker) {
      return;
    }

    const statusTrackerCells = indexStatusTrackerCells(dpr.dprStatusTracker);

    statusTrackerAccumulations.forEach(
      ({ statusTrackerId, valuesByColumnId }) =>
        valuesByColumnId.forEach((rowValues, columnId) =>
          rowValues.forEach((value, rowId) =>
            this.setStatusTrackerValue(
              dpr.dprStatusTracker,
              statusTrackerCells,
              statusTrackerId,
              rowId,
              columnId,
              value
            )
          )
        )
    );
  }

  /**
   * Formulas run after the carry-overs, so they read the carry-overs this DPR
   * prints. They keep 10 decimals, as the API does, because the DPR after this
   * one starts from them. In a section without history the formulas that read
   * the DPR before keep what is stored: calculated, they would come out as if
   * the history were 0.
   */
  private fillFormulas(
    projectSetup: ProjectSetup,
    dpr: DprDetails,
    tableOfColumnsHistory: TableOfColumnHistoryItem[],
    statusTrackerHistory: StatusTrackerHistoryItem[],
    tableOfColumnsAccumulations: Map<number, DprTableOfColumnsAccumulation>,
    statusTrackerAccumulations: Map<number, DprStatusTrackerAccumulation>,
    sectionsWithoutHistory: DprSectionsWithoutHistory
  ): void {
    const tableOfColumnsContexts =
      this.dprFormulaCalculationService.createTableOfColumnsContexts(
        projectSetup.tableOfColumns,
        dpr.dprDateTime,
        tableOfColumnsHistory
      );

    dpr.dprTableOfColumns.forEach((dprTableOfColumn) => {
      const tableOfColumnId = +dprTableOfColumn.tableOfColumnId;
      const context = tableOfColumnsContexts.get(tableOfColumnId);
      const details = dprTableOfColumn.dprTableOfColumnsDetails ?? [];

      if (!context || !details.length) {
        return;
      }

      const detailsById = new Map(
        details.map((detail) => [String(detail.id), detail])
      );
      const skippedColumnIds = sectionsWithoutHistory.tableOfColumnIds.has(
        tableOfColumnId
      )
        ? getHistoryColumnIds(
            context.formulas,
            new Set(
              tableOfColumnsAccumulations.get(tableOfColumnId)
                ?.carryOverItemIds ?? []
            )
          )
        : new Set<number>();

      this.dprFormulaCalculationService
        .calculateTableOfColumnsDetails(
          context,
          details,
          FormulaPreviousValuePrecision
        )
        .forEach((values, rowId) =>
          values.forEach((value, itemId) => {
            if (!skippedColumnIds.has(itemId)) {
              this.setTableOfColumnValue(detailsById.get(rowId), itemId, value);
            }
          })
        );
    });

    const statusTrackerCells = indexStatusTrackerCells(dpr.dprStatusTracker);

    this.dprFormulaCalculationService
      .createStatusTrackerContexts(
        projectSetup.statusTrackers,
        dpr.dprDateTime,
        statusTrackerHistory
      )
      .forEach((context) => {
        const skippedColumnIds = sectionsWithoutHistory.includesStatusTracker
          ? getHistoryColumnIds(
              context.formulas,
              new Set(
                statusTrackerAccumulations
                  .get(context.statusTrackerId)
                  ?.valuesByColumnId.keys() ?? []
              )
            )
          : new Set<number>();

        this.dprFormulaCalculationService
          .calculateStatusTrackerDetails(
            context,
            dpr.dprStatusTracker.dprStatusTrackerDetails ?? [],
            FormulaPreviousValuePrecision
          )
          .forEach((values, rowId) =>
            values.forEach((value, columnId) => {
              if (!skippedColumnIds.has(columnId)) {
                this.setStatusTrackerValue(
                  dpr.dprStatusTracker,
                  statusTrackerCells,
                  context.statusTrackerId,
                  rowId,
                  columnId,
                  value
                );
              }
            })
          );
      });
  }

  private setTableOfColumnValue(
    detail: DprTableOfColumnDetail | undefined,
    tableOfColumnItemId: number,
    value: string | null
  ): void {
    const detailItem = detail?.dprTableOfColumnDetailItems?.find(
      (item) => item.tableOfColumnItemId === tableOfColumnItemId
    );

    if (detailItem) {
      detailItem.value = value;
      return;
    }

    if (!detail || value == null) {
      return;
    }

    detail.dprTableOfColumnDetailItems ??= [];
    detail.dprTableOfColumnDetailItems.push({
      id: null,
      dprTableOfColumnDetailId: detail.id,
      tableOfColumnItemId,
      value,
    });
  }

  private setStatusTrackerValue(
    dprStatusTracker: DprStatusTracker,
    statusTrackerCells: Map<string, DprStatusTrackerDetails>,
    statusTrackerId: number,
    rowId: number | string,
    columnId: number,
    value: string | null
  ): void {
    const key = getStatusTrackerCellKey(statusTrackerId, rowId, columnId);
    const detail = statusTrackerCells.get(key);

    if (detail) {
      detail.value = value;
      return;
    }

    if (value == null) {
      return;
    }

    const newDetail: DprStatusTrackerDetails = {
      id: null,
      dprStatusTrackerId: dprStatusTracker.id,
      rowId: +rowId,
      columnId,
      colorTypeId: null,
      value,
      statusTrackerId,
    };

    dprStatusTracker.dprStatusTrackerDetails ??= [];
    dprStatusTracker.dprStatusTrackerDetails.push(newDetail);
    statusTrackerCells.set(key, newDetail);
  }

  //#endregion
}
